import React from 'react';
import { EmptyState } from '../../components/ui/EmptyState';
import { Panel } from '../../components/ui/Panel';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { getOnboardingKycStatus, submitFleetManagerKyc } from '../../services/fleet';
import { User } from '../../types/app';

type FleetKycRequiredProps = {
  onSubmitted?: (status?: string) => void;
  user: User | null;
};

type FleetKycForm = {
  operatorType: 'individual' | 'company';
  fullName: string;
  phoneNumber: string;
  nationalId: string;
  companyName: string;
  companyRegNumber: string;
};

export function FleetKycRequired({ onSubmitted, user }: FleetKycRequiredProps) {
  const [form, setForm] = React.useState<FleetKycForm>({
    operatorType: 'individual',
    fullName: user?.name ?? '',
    phoneNumber: '',
    nationalId: '',
    companyName: '',
    companyRegNumber: '',
  });
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [remoteStatus, setRemoteStatus] = React.useState('');
  const status = normalizeStatus(remoteStatus || user?.kycStatus || user?.onboardingStatus);
  const hasSubmitted = status === 'pending' || status === 'submitted' || message !== '';
  const isCompany = form.operatorType === 'company';
  const onSubmittedRef = React.useRef(onSubmitted);
  const statusRef = React.useRef(status);
  const [shouldPollStatus, setShouldPollStatus] = React.useState(false);

  React.useEffect(() => {
    onSubmittedRef.current = onSubmitted;
  }, [onSubmitted]);

  React.useEffect(() => {
    statusRef.current = status;
  }, [status]);

  React.useEffect(() => {
    let isActive = true;

    const loadStatus = async () => {
      try {
        const response = await getOnboardingKycStatus();
        if (!isActive) return;

        const nextStatus = normalizeStatus(
          readString(response, 'status')
          ?? readString(response, 'kyc_status')
          ?? readString(response, 'onboarding_status'),
        ) ?? '';

        setRemoteStatus(nextStatus);
        setShouldPollStatus(isReviewStatus(nextStatus));

        if (nextStatus && nextStatus !== statusRef.current) {
          onSubmittedRef.current?.(nextStatus);
        }
      } catch {
        if (isActive) {
          setRemoteStatus('');
          setShouldPollStatus(false);
        }
      }
    };

    void loadStatus();
    return () => {
      isActive = false;
    };
  }, []);

  React.useEffect(() => {
    if (!shouldPollStatus) {
      return undefined;
    }

    let isActive = true;
    const timer = window.setInterval(() => {
      getOnboardingKycStatus()
        .then((response) => {
          if (!isActive) return;

          const nextStatus = normalizeStatus(
            readString(response, 'status')
            ?? readString(response, 'kyc_status')
            ?? readString(response, 'onboarding_status'),
          ) ?? '';
          setRemoteStatus(nextStatus);
          setShouldPollStatus(isReviewStatus(nextStatus));

          if (nextStatus && nextStatus !== statusRef.current) {
            onSubmittedRef.current?.(nextStatus);
          }
        })
        .catch(() => {
          if (isActive) {
            setShouldPollStatus(false);
          }
        });
    }, 15000);

    return () => {
      isActive = false;
      window.clearInterval(timer);
    };
  }, [shouldPollStatus]);

  const updateField = (key: keyof FleetKycForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submitKyc = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (isCompany && (!form.companyName.trim() || !form.companyRegNumber.trim())) {
      setError('Company name and registration number are required for company operators.');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFleetManagerKyc({
        operatorType: form.operatorType,
        fullName: form.fullName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        nationalId: form.nationalId.trim(),
        companyName: isCompany ? form.companyName.trim() : null,
        companyRegNumber: isCompany ? form.companyRegNumber.trim() : null,
      });
      setRemoteStatus('pending');
      setShouldPollStatus(true);
      setMessage('KYC submitted successfully. An administrator will review your onboarding details.');
      onSubmitted?.('pending');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to submit KYC');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fleet-dashboard">
      <SectionHeader
        title="Fleet access pending"
        subtitle="Your fleet manager dashboard unlocks after KYC is submitted and approved."
      />

      <Panel className="fleet-kyc-required">
        <EmptyState
          className="empty fleet-kyc-required-state"
          title="KYC approval required"
          message="You cannot access fleet operations until your KYC has been submitted and approved by an administrator."
        />
        <dl className="kyc-details">
          <div>
            <dt>Account</dt>
            <dd>{user?.email || 'Fleet manager'}</dd>
          </div>
          <div>
            <dt>KYC status</dt>
            <dd>{formatStatus(status)}</dd>
          </div>
        </dl>
      </Panel>

      <Panel className="fleet-kyc-submit-panel">
        <div className="fleet-panel-head">
          <div>
            <h2>Submit fleet manager KYC</h2>
            <span>Register your fleet operator profile, then submit it for admin review.</span>
          </div>
        </div>

        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
        {message ? <div className="kyc-message" role="status">{message}</div> : null}
        {hasSubmitted && !message ? (
          <div className="kyc-message" role="status">
            Your KYC has already been submitted and is waiting for admin review.
          </div>
        ) : null}

        <form className="fleet-kyc-form" onSubmit={submitKyc}>
          <div className="fleet-kyc-form-grid">
            <label className="fleet-kyc-field">
              <span>Operator type</span>
              <select
                disabled={isSubmitting || hasSubmitted}
                onChange={(event) => updateField('operatorType', event.target.value)}
                value={form.operatorType}
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </label>
            <KycInput
              autoComplete="name"
              disabled={isSubmitting || hasSubmitted}
              label="Full name"
              onChange={(value) => updateField('fullName', value)}
              required
              value={form.fullName}
            />
            <KycInput
              autoComplete="tel"
              disabled={isSubmitting || hasSubmitted}
              label="Phone number"
              onChange={(value) => updateField('phoneNumber', value)}
              required
              type="tel"
              value={form.phoneNumber}
            />
            <KycInput
              disabled={isSubmitting || hasSubmitted}
              label="National ID number"
              onChange={(value) => updateField('nationalId', value)}
              required
              value={form.nationalId}
            />
            <KycInput
              disabled={isSubmitting || hasSubmitted}
              label="Company name"
              onChange={(value) => updateField('companyName', value)}
              required={isCompany}
              value={form.companyName}
            />
            <KycInput
              disabled={isSubmitting || hasSubmitted}
              label="Company registration number"
              onChange={(value) => updateField('companyRegNumber', value)}
              required={isCompany}
              value={form.companyRegNumber}
            />
          </div>

          <div className="kyc-dialog-actions">
            <button className="btn btn-primary" disabled={isSubmitting || hasSubmitted} type="submit">
              {isSubmitting ? 'Submitting...' : hasSubmitted ? 'Submitted for review' : 'Submit KYC'}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

type KycInputProps = {
  autoComplete?: string;
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  value: string;
};

function KycInput({ autoComplete, disabled, label, onChange, required = false, type = 'text', value }: KycInputProps) {
  const id = React.useId();
  return (
    <label className="fleet-kyc-field" htmlFor={id}>
      <span>{label}</span>
      <input
        autoComplete={autoComplete}
        disabled={disabled}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        type={type}
        value={value}
      />
    </label>
  );
}

function normalizeStatus(status: string | undefined) {
  return status?.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function isReviewStatus(status: string | undefined) {
  return status === 'pending'
    || status === 'submitted'
    || status === 'in_review'
    || status === 'under_review'
    || status === 'review_pending'
    || status === 'processing';
}

function readString(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function formatStatus(status: string | undefined) {
  if (!status) return 'not submitted';
  return status.replace(/_/g, ' ');
}
